import { Alert, Button, Card, Descriptions, Form, Modal, Select, Skeleton, Space, Tag, Typography } from 'antd'
import { useState } from 'react'
import { useSidebarTenantsQuery } from '../../MyTenant/tenantsQueries'
import { filterTenantOption, toTenantOptions } from '../../MyTenant/tenantSelectModel'
import { useAdminMerchantStatusQuery, useResetPaymentsApplicationMutation } from './paymentsResetQueries'
import {
  formatStatusLabel,
  getApplicationStatusColor,
  getMerchantStatusColor,
  getResetBlockedReason,
  getResetErrorMessage,
} from './paymentsResetModel'

const { Paragraph, Text } = Typography

const PaymentsResetSection = () => {
  const [modal, modalContextHolder] = Modal.useModal()
  const [selectedTenantId, setSelectedTenantId] = useState<number | undefined>(undefined)
  const tenantsQuery = useSidebarTenantsQuery()
  const statusQuery = useAdminMerchantStatusQuery(selectedTenantId)
  const mutation = useResetPaymentsApplicationMutation()

  const tenantName = tenantsQuery.data?.find(tenant => tenant.id === selectedTenantId)?.name ?? ''
  const status = statusQuery.data
  const blockedReason = status ? getResetBlockedReason(status) : null

  const handleTenantChange = (tenantId: number) => {
    setSelectedTenantId(tenantId)
    mutation.reset()
  }

  const handleReset = () => {
    if (selectedTenantId === undefined) {
      return
    }
    modal.confirm({
      title: `Reset the payments application for ${tenantName}?`,
      okText: 'Reset',
      cancelText: 'Cancel',
      okButtonProps: { danger: true },
      width: 540,
      content: (
        <div>
          <Paragraph style={{ marginBottom: 8 }}>
            This soft-deletes the tenant&apos;s merchant record and clears cached Rainforest
            sessions. The old merchant and application data are kept for reference.
          </Paragraph>
          <Paragraph style={{ marginBottom: 0 }}>
            Afterwards the tenant starts over with a brand-new application through the normal
            Start Application flow.
          </Paragraph>
        </div>
      ),
      onOk: () => {
        mutation.mutate(selectedTenantId)
      },
    })
  }

  return (
    <Card
      title="Reset Payments Application"
      style={{ maxWidth: 760 }}
      extra={
        <Button
          size="small"
          onClick={() => statusQuery.refetch()}
          loading={statusQuery.isFetching}
          disabled={selectedTenantId === undefined}
        >
          Refresh
        </Button>
      }
    >
      {modalContextHolder}
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          Resets a tenant&apos;s SynkedUP Payments application after Rainforest&apos;s 120-day
          expiry/decline, so the tenant can reapply through the normal Start Application flow.
        </Paragraph>

        <Form layout="vertical" style={{ marginBottom: 0 }}>
          <Form.Item label="Tenant" style={{ marginBottom: 0 }}>
            <Select
              showSearch
              placeholder="Search for a tenant"
              value={selectedTenantId}
              onChange={handleTenantChange}
              options={toTenantOptions(tenantsQuery.data ?? [])}
              filterOption={filterTenantOption}
              loading={tenantsQuery.isLoading}
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Form>

        {statusQuery.isLoading && selectedTenantId !== undefined && <Skeleton active />}

        {statusQuery.isError && (
          <Alert
            type="error"
            showIcon
            message="Failed to load the merchant status"
            description="Refresh to try again."
          />
        )}

        {status && (
          <>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Tenant">
                <Text strong>{tenantName}</Text> (ID {selectedTenantId})
              </Descriptions.Item>
              <Descriptions.Item label="Merchant status">
                <Space>
                  <Tag color={getMerchantStatusColor(status.merchantStatus)}>
                    {formatStatusLabel(status.merchantStatus)}
                  </Tag>
                  {status.isSandbox && <Tag>Sandbox</Tag>}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Merchant ID">
                {status.merchantId === '' ? '—' : <Text code>{status.merchantId}</Text>}
              </Descriptions.Item>
              <Descriptions.Item label="Application status">
                <Tag color={getApplicationStatusColor(status.applicationStatus)}>
                  {formatStatusLabel(status.applicationStatus)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Application ID">
                {status.applicationId === '' ? '—' : <Text code>{status.applicationId}</Text>}
              </Descriptions.Item>
            </Descriptions>

            {blockedReason !== null && !mutation.isSuccess && (
              <Alert type="warning" showIcon message={blockedReason} />
            )}

            <Button
              type="primary"
              danger
              onClick={handleReset}
              loading={mutation.isLoading}
              disabled={blockedReason !== null}
            >
              Reset payments application
            </Button>
          </>
        )}

        {mutation.isSuccess && (
          <Alert
            type="success"
            showIcon
            message="Payments application reset"
            description={`${tenantName} can now reapply through the Start Application flow on the Invoicing settings page.`}
          />
        )}

        {mutation.isError && (
          <Alert
            type="error"
            showIcon
            message="Reset failed"
            description={getResetErrorMessage(mutation.error)}
          />
        )}
      </Space>
    </Card>
  )
}

export default PaymentsResetSection
